import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const CurrencyContext = createContext(null);

// Static fallback (if backend FX is unreachable). Keys & flags stay client-side for label/symbol display.
const FALLBACK_CURRENCIES = {
  INR: { symbol: "₹",  rate: 1,       label: "Indian Rupee",       flag: "🇮🇳" },
  USD: { symbol: "$",  rate: 0.012,   label: "US Dollar",          flag: "🇺🇸" },
  EUR: { symbol: "€",  rate: 0.011,   label: "Euro",               flag: "🇪🇺" },
  GBP: { symbol: "£",  rate: 0.0094,  label: "British Pound",      flag: "🇬🇧" },
  AED: { symbol: "د.إ",rate: 0.044,   label: "UAE Dirham",         flag: "🇦🇪" },
  SGD: { symbol: "S$", rate: 0.016,   label: "Singapore Dollar",   flag: "🇸🇬" },
  CAD: { symbol: "C$", rate: 0.016,   label: "Canadian Dollar",    flag: "🇨🇦" },
  AUD: { symbol: "A$", rate: 0.018,   label: "Australian Dollar",  flag: "🇦🇺" },
};

function detectDefault() {
  try {
    const saved = localStorage.getItem("optivize_currency");
    if (saved) return saved;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.includes("Kolkata") || tz.includes("Calcutta")) return "INR";
    if (tz.includes("London")) return "GBP";
    if (tz.includes("Dubai")) return "AED";
    if (tz.includes("Singapore")) return "SGD";
    if (tz.includes("Sydney") || tz.includes("Melbourne")) return "AUD";
    if (tz.includes("Toronto") || tz.includes("Vancouver")) return "CAD";
    if (tz.includes("Tokyo")) return "JPY";
    if (tz.includes("Hong_Kong")) return "HKD";
    if (tz.includes("Shanghai") || tz.includes("Beijing")) return "CNY";
    if (tz.includes("Seoul")) return "KRW";
    if (tz.includes("Europe")) return "EUR";
    if (tz.includes("Sao_Paulo") || tz.includes("America/Argentina")) return "BRL";
    return "USD";
  } catch {
    return "USD";
  }
}

export function CurrencyProvider({ children }) {
  const [code, setCode] = useState(detectDefault);
  const [currencies, setCurrencies] = useState(FALLBACK_CURRENCIES);
  const [fetchedAt, setFetchedAt] = useState(null);

  useEffect(() => {
    localStorage.setItem("optivize_currency", code);
  }, [code]);

  useEffect(() => {
    let cancelled = false;
    api.get("/fx/rates").then((res) => {
      if (cancelled) return;
      const { rates, currencies: meta, fetched_at } = res.data;
      const merged = {};
      Object.entries(meta || {}).forEach(([c, info]) => {
        if (rates[c] !== undefined) {
          merged[c] = {
            symbol: info.symbol || "",
            rate: rates[c],
            label: info.label,
            flag: info.flag || "🏳️",
          };
        }
      });
      // Always keep INR available
      if (!merged.INR) merged.INR = FALLBACK_CURRENCIES.INR;
      if (Object.keys(merged).length > 0) setCurrencies(merged);
      setFetchedAt(fetched_at);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const setCurrency = (c) => { if (currencies[c]) setCode(c); };
  const current = currencies[code] || currencies.USD || FALLBACK_CURRENCIES.USD;

  const convert = (inrAmount) => Math.round(inrAmount * current.rate * 100) / 100;

  const format = (inrAmount) => {
    const v = convert(inrAmount);
    if (code === "INR" || code === "JPY" || code === "VND" || code === "IDR" || code === "KRW") {
      return `${current.symbol}${Math.round(v).toLocaleString(code === "INR" ? "en-IN" : "en-US")}`;
    }
    const display = v >= 100 ? Math.round(v).toLocaleString("en-US") : v.toFixed(2);
    return `${current.symbol}${display}`;
  };

  return (
    <CurrencyContext.Provider value={{
      code, setCurrency, current, format, convert,
      list: Object.entries(currencies).map(([code, info]) => ({ code, ...info })),
      fetchedAt,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => useContext(CurrencyContext);
